import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip
} from '@mui/material';
import { Save, Check, Schedule, SportsFootball, SportsBasketball, ArrowBack, Visibility, Lock, Refresh } from '@mui/icons-material';
import { SportIcon } from '../utils/sportIcons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
import { weeksAPI, gamesAPI, picksAPI, tiebreakersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useSport } from '../contexts/SportContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Week, Game, Pick, GameWithPick, PartialPick, TiebreakerGame, TiebreakerPrediction } from '../types';
import { getSpreadDisplay } from '../utils/gameUtils';

const MakePicks: React.FC = () => {
  const { user } = useAuth();
  const { selectedSport: sport } = useSport();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const weekParam = searchParams.get('week');
  const [weeks, setWeeks] = useState<Week[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<Week | null>(null);
  const [games, setGames] = useState<GameWithPick[]>([]);
  const [userPicks, setUserPicks] = useState<Pick[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [joebRuleDialog, setJoebRuleDialog] = useState(false);
  const [viewingPreviousWeek, setViewingPreviousWeek] = useState(false);
  const [tiebreakerGames, setTiebreakerGames] = useState<TiebreakerGame[]>([]);
  const [tiebreakerPredictions, setTiebreakerPredictions] = useState<{[key: number]: number}>({});

  useEffect(() => {
    loadWeeks();
  }, [sport]);

  useEffect(() => {
    if (selectedWeek) {
      loadGamesAndPicks();
    }
  }, [selectedWeek]);

  const loadWeeks = async () => {
    try {
      setLoading(true);
      const weeksData = await weeksAPI.getWeeks({ sport });
      setWeeks(weeksData);
      
      // If week parameter is provided, find and select that week
      if (weekParam) {
        const targetWeek = weeksData.find((week: Week) => week.id === parseInt(weekParam));
        if (targetWeek) {
          setSelectedWeek(targetWeek);
          setViewingPreviousWeek(!targetWeek.is_active);
          return;
        }
      }
      
      // Auto-select current active week
      const activeWeek = weeksData.find((week: Week) => week.is_active);
      if (activeWeek) {
        setSelectedWeek(activeWeek);
        setViewingPreviousWeek(false);
      } else if (weeksData.length > 0) {
        // If no active week, select the most recent one
        const sortedWeeks = weeksData.sort((a, b) => {
          if (a.year !== b.year) return b.year - a.year;
          return b.week_number - a.week_number;
        });
        setSelectedWeek(sortedWeeks[0]);
        setViewingPreviousWeek(true);
      }
    } catch (error) {
      console.error('Error loading weeks:', error);
      setError('Failed to load weeks');
    } finally {
      setLoading(false);
    }
  };

  const loadGamesAndPicks = async () => {
    if (!selectedWeek) return;

    try {
      setLoading(true);
      const gamesData = await gamesAPI.getGamesForWeek(selectedWeek.id);
      
      // For previous weeks, try to get user's picks, but don't fail if locked
      let picksData: Pick[] = [];
      try {
        picksData = await picksAPI.getPicksForWeek(selectedWeek.id);
      } catch (pickError) {
        console.log('Could not load user picks (may be viewing previous week):', pickError);
        // For previous weeks, this is expected if picks are locked
      }

      // Load tiebreaker games
      let tiebreakerData: TiebreakerGame[] = [];
      let tiebreakerPredictionsData: TiebreakerPrediction[] = [];
      try {
        tiebreakerData = await tiebreakersAPI.getTiebreakers(selectedWeek.id);
        tiebreakerPredictionsData = await tiebreakersAPI.getPredictions(selectedWeek.id);
      } catch (tiebreakerError) {
        console.log('Could not load tiebreakers (may not exist or viewing previous week):', tiebreakerError);
      }

      // Create a map of user picks by game_id
      const picksMap = new Map(picksData.map((pick: Pick) => [pick.game_id, pick]));

      // Create a map of tiebreaker predictions by tiebreaker_game_id
      const tiebreakerPredictionsMap = new Map(
        tiebreakerPredictionsData.map((pred: TiebreakerPrediction) => [pred.tiebreaker_game_id, pred.predicted_total_points])
      );

      // Combine games with user picks
      const gamesWithPicks: GameWithPick[] = gamesData.map((game: Game) => ({
        ...game,
        user_pick: picksMap.get(game.id)
      }));

      setGames(gamesWithPicks);
      setUserPicks(picksData);
      setTiebreakerGames(tiebreakerData);
      setTiebreakerPredictions(Object.fromEntries(tiebreakerPredictionsMap));
    } catch (error) {
      console.error('Error loading games and picks:', error);
      setError('Failed to load games and picks');
    } finally {
      setLoading(false);
    }
  };

  const handlePickChange = (gameId: number, pickedTeam: string) => {
    setGames(prevGames => 
      prevGames.map(game => {
        if (game.id === gameId) {
          const updatedGame: GameWithPick = {
            ...game,
            user_pick: {
              id: game.user_pick?.id,
              game_id: gameId,
              user_id: user?.id || 0,
              selected_team: pickedTeam,
              is_correct: game.user_pick?.is_correct,
              created_at: game.user_pick?.created_at,
              updated_at: game.user_pick?.updated_at
            }
          };
          return updatedGame;
        }
        return game;
      })
    );
  };

  const handleTiebreakerPredictionChange = (tiebreakerGameId: number, prediction: string) => {
    const numericPrediction = parseInt(prediction);
    if (prediction === '' || (!isNaN(numericPrediction) && numericPrediction >= 0 && numericPrediction <= 200)) {
      setTiebreakerPredictions(prev => ({
        ...prev,
        [tiebreakerGameId]: prediction === '' ? 0 : numericPrediction
      }));
    }
  };

  const handleSavePicksClick = () => {
    if (!selectedWeek) return;

    // JoeB Rule #1: Check if user has made picks for all games
    const totalGames = games.length;
    const pickedGames = games.filter(game => game.user_pick?.selected_team).length;
    const missingPicks = totalGames - pickedGames;

    if (missingPicks > 0) {
      // Show JoeB Rule #1 warning dialog
      setJoebRuleDialog(true);
    } else {
      // All picks made, proceed with normal confirmation
      setConfirmDialog(true);
    }
  };

  const proceedWithIncompletePicks = () => {
    setJoebRuleDialog(false);
    setConfirmDialog(true);
  };

  const savePicks = async () => {
    if (!selectedWeek) return;

    try {
      setSaving(true);
      setError(null);

      // Collect all picks that have been made
      const picksToSubmit = games
        .filter(game => game.user_pick?.selected_team)
        .map(game => ({
          game_id: game.id,
          selected_team: game.user_pick!.selected_team!
        }));

      // Collect tiebreaker predictions
      const tiebreakerPredictionsToSubmit = tiebreakerGames
        .filter(tiebreaker => tiebreakerPredictions[tiebreaker.id] !== undefined && tiebreakerPredictions[tiebreaker.id] > 0)
        .map(tiebreaker => ({
          tiebreaker_game_id: tiebreaker.id,
          predicted_total_points: tiebreakerPredictions[tiebreaker.id]
        }));

      if (picksToSubmit.length === 0 && tiebreakerPredictionsToSubmit.length === 0) {
        setError('Please make at least one pick or tiebreaker prediction before saving');
        return;
      }

      // Save regular picks if any
      if (picksToSubmit.length > 0) {
        await picksAPI.submitMultiplePicks(picksToSubmit);
      }

      // Save tiebreaker predictions if any
      if (tiebreakerPredictionsToSubmit.length > 0) {
        await tiebreakersAPI.submitPredictions(selectedWeek.id, tiebreakerPredictionsToSubmit);
      }

      const totalSaved = picksToSubmit.length + tiebreakerPredictionsToSubmit.length;
      setSuccess(`Successfully saved ${picksToSubmit.length} picks and ${tiebreakerPredictionsToSubmit.length} tiebreaker predictions!`);
      setConfirmDialog(false);
      
      // Reload to get updated data
      await loadGamesAndPicks();
    } catch (error: any) {
      console.error('Error saving picks:', error);
      setError(error.response?.data?.error || 'Failed to save picks');
    } finally {
      setSaving(false);
    }
  };

  const getPicksProgress = () => {
    const totalGames = games.length;
    const pickedGames = games.filter(game => game.user_pick?.selected_team).length;
    return { picked: pickedGames, total: totalGames };
  };

  const isPicksLocked = () => {
    if (!selectedWeek) return false;
    return dayjs.utc().isAfter(dayjs.utc(selectedWeek.picks_lock_time));
  };

  const getTimeUntilLock = () => {
    if (!selectedWeek) return '';
    
    // Parse lock time as UTC since database stores UTC timestamps
    const lockTime = dayjs.utc(selectedWeek.picks_lock_time);
    const now = dayjs.utc();
    
    if (now.isAfter(lockTime)) {
      return 'Picks are locked';
    }
    
    const diff = lockTime.diff(now);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} day${days !== 1 ? 's' : ''} until lock`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m until lock`;
    } else {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} until lock`;
    }
  };

  const formatGameTime = (gameTime: string) => {
    // Parse date and convert to Eastern Time for display
    let date = dayjs(gameTime);
    
    // If no timezone info, assume UTC
    if (!gameTime.includes('Z') && !gameTime.includes('+') && !gameTime.includes('-')) {
      date = dayjs.utc(gameTime);
    }
    
    return date.tz('America/New_York').format('ddd M/D h:mm A z');
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedWeek) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Make Picks
        </Typography>
        <Alert severity="info">
          No active weeks available for picks at this time.
        </Alert>
      </Box>
    );
  }

  const progress = getPicksProgress();
  const locked = isPicksLocked();

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: { sm: 'space-between' }, 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: 2,
        mb: 3 
      }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
          {viewingPreviousWeek && (
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/dashboard')}
              variant="outlined"
              size="small"
              fullWidth={false}
            >
              Back to Dashboard
            </Button>
          )}
          <Box>
            <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              {viewingPreviousWeek ? 'View Picks' : 'Make Picks'} - Week {selectedWeek.week_number} ({selectedWeek.year})
            </Typography>
            {viewingPreviousWeek && (
              <Chip
                label="Previous Week"
                size="small"
                color="info"
                icon={<Visibility />}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          <Chip 
            icon={locked ? <Lock /> : <Schedule />}
            label={locked ? '🔒 PICKS LOCKED' : getTimeUntilLock()}
            color={locked ? 'error' : 'primary'}
            variant={locked ? 'filled' : 'outlined'}
            sx={locked ? { 
              fontWeight: 'bold',
              '& .MuiChip-label': { fontWeight: 'bold' }
            } : {}}
          />
          <Tooltip title="Refresh lock status">
            <IconButton 
              size="small" 
              onClick={() => window.location.reload()}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
          <Chip
            icon={selectedWeek ? <SportIcon sport={selectedWeek.sport} /> : <SportsFootball />}
            label={`${progress.picked}/${progress.total} picks${progress.picked < progress.total ? ' (JoeB Rule #1: Complete all picks!)' : ''}`}
            color={progress.picked === progress.total ? 'success' : 'warning'}
            sx={{
              fontSize: progress.picked < progress.total ? '0.9rem' : '0.875rem',
              fontWeight: progress.picked < progress.total ? 'bold' : 'normal'
            }}
          />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {locked && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2, 
            '& .MuiAlert-message': { 
              fontWeight: 'bold',
              fontSize: '1.1rem'
            }
          }}
          icon={<Lock />}
        >
          🔒 PICKS ARE LOCKED - You can view your selections but cannot make changes.
        </Alert>
      )}

      <Box mb={2}>
        <LinearProgress 
          variant="determinate" 
          value={(progress.picked / progress.total) * 100}
          sx={{ height: 8, borderRadius: 4 }}
        />
        <Typography variant="body2" color="text.secondary" mt={1}>
          {progress.picked} of {progress.total} games picked
        </Typography>
        
        {/* Debug info - can be removed later */}
        {selectedWeek && (
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.7rem', opacity: 0.7, mt: 0.5 }}>
            Debug: Current time: {dayjs().tz('America/New_York').format('YYYY-MM-DD HH:mm:ss z')} | Lock time: {(() => {
              let date = dayjs(selectedWeek.picks_lock_time);
              if (!selectedWeek.picks_lock_time.includes('Z') && !selectedWeek.picks_lock_time.includes('+') && !selectedWeek.picks_lock_time.includes('-')) {
                date = dayjs.utc(selectedWeek.picks_lock_time);
              }
              return date.tz('America/New_York').format('YYYY-MM-DD HH:mm:ss z');
            })()} | Locked: {locked ? 'YES' : 'NO'}
          </Typography>
        )}
      </Box>

      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: { 
          xs: '1fr', 
          sm: 'repeat(auto-fit, minmax(280px, 1fr))',
          md: 'repeat(auto-fit, minmax(320px, 1fr))'
        },
        gap: { xs: 2, sm: 1 }
      }}>
        {games.map((game) => (
          <Card key={game.id} variant="outlined" sx={{ 
            '& .MuiCardContent-root': { 
              padding: { xs: '12px', sm: '8px 12px' },
              '&:last-child': { paddingBottom: { xs: '12px', sm: '8px' } }
            }
          }}>
            <CardContent>
              {/* Team Selection Buttons - Top */}
              <Box display="flex" gap={0.5} sx={{ mb: 1 }}>
                <Box
                  onClick={() => !locked && !viewingPreviousWeek && handlePickChange(game.id, game.away_team)}
                  sx={{
                    flex: 1,
                    height: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 1,
                    borderColor: game.user_pick?.selected_team === game.away_team ? 'primary.main' : 'grey.300',
                    bgcolor: game.user_pick?.selected_team === game.away_team ? 'primary.light' : 'background.paper',
                    color: game.user_pick?.selected_team === game.away_team ? 'primary.contrastText' : 'text.primary',
                    borderRadius: 1,
                    cursor: locked || viewingPreviousWeek ? 'default' : 'pointer',
                    opacity: locked || viewingPreviousWeek ? 0.6 : 1,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: locked || viewingPreviousWeek ? undefined : 
                        game.user_pick?.selected_team === game.away_team ? 'primary.light' : 'action.hover',
                      borderColor: locked || viewingPreviousWeek ? undefined : 'primary.main'
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ 
                    fontSize: { xs: '0.85rem', sm: '0.8rem' }, 
                    fontWeight: 600,
                    textAlign: 'center',
                    px: 0.5
                  }}>
                    {game.away_team}
                  </Typography>
                </Box>
                
                <Box
                  onClick={() => !locked && !viewingPreviousWeek && handlePickChange(game.id, game.home_team)}
                  sx={{
                    flex: 1,
                    height: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 1,
                    borderColor: game.user_pick?.selected_team === game.home_team ? 'primary.main' : 'grey.300',
                    bgcolor: game.user_pick?.selected_team === game.home_team ? 'primary.light' : 'background.paper',
                    color: game.user_pick?.selected_team === game.home_team ? 'primary.contrastText' : 'text.primary',
                    borderRadius: 1,
                    cursor: locked || viewingPreviousWeek ? 'default' : 'pointer',
                    opacity: locked || viewingPreviousWeek ? 0.6 : 1,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: locked || viewingPreviousWeek ? undefined : 
                        game.user_pick?.selected_team === game.home_team ? 'primary.light' : 'action.hover',
                      borderColor: locked || viewingPreviousWeek ? undefined : 'primary.main'
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ 
                    fontSize: { xs: '0.85rem', sm: '0.8rem' }, 
                    fontWeight: 600,
                    textAlign: 'center',
                    px: 0.5
                  }}>
                    {game.home_team}
                  </Typography>
                </Box>
              </Box>

              {/* Game Info - Bottom */}
              <Box>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.2, color: 'text.secondary', textAlign: 'center' }}>
                  {game.away_team} @ {game.home_team}
                </Typography>
                <Box display="flex" gap={1} alignItems="center" justifyContent="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {formatGameTime(game.game_time)}
                  </Typography>
                  {game.spread && (
                    <Chip 
                      label={getSpreadDisplay(game)}
                      size="small" 
                      color="info"
                      variant="outlined"
                      sx={{ 
                        height: 16, 
                        fontSize: '0.6rem', 
                        fontWeight: 600,
                        '& .MuiChip-label': { px: 0.5 },
                        bgcolor: 'info.light',
                        color: 'info.contrastText'
                      }} 
                    />
                  )}
                  {game.is_spread_pick && (
                    <Chip label="S" size="small" color="primary" sx={{ height: 12, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.3 } }} />
                  )}
                  {game.user_pick?.selected_team && (
                    <Check color="success" sx={{ fontSize: 12 }} />
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Tiebreaker Games Section */}
      {tiebreakerGames.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedWeek && <SportIcon sport={selectedWeek.sport} />}
            Tiebreaker Games
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Predict the total points scored in these games to break ties.
          </Typography>

          <Box sx={{ display: 'grid', gap: { xs: 1, sm: 0.5 } }}>
            {tiebreakerGames.map((tiebreaker) => (
              <Card key={tiebreaker.id} variant="outlined" sx={{ 
                bgcolor: 'action.hover',
                '& .MuiCardContent-root': { 
                  padding: '8px 12px !important',
                  '&:last-child': { paddingBottom: '8px !important' }
                }
              }}>
                <CardContent>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: { sm: 'space-between' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 1, sm: 0 }
                  }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 600, 
                        fontSize: { xs: '0.85rem', sm: '0.8rem' }, 
                        lineHeight: 1.2 
                      }}>
                        TB{tiebreaker.tiebreaker_order}: {tiebreaker.away_team} @ {tiebreaker.home_team}
                      </Typography>
                      <Box display="flex" gap={1} alignItems="center" sx={{ mt: 0.25 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {formatGameTime(tiebreaker.game_time)}
                        </Typography>
                        {tiebreaker.spread && (
                          <Chip 
                            label={getSpreadDisplay(tiebreaker)}
                            size="small" 
                            color="info"
                            variant="outlined"
                            sx={{ 
                              height: 14, 
                              fontSize: '0.55rem', 
                              fontWeight: 600,
                              '& .MuiChip-label': { px: 0.4 },
                              bgcolor: 'info.light',
                              color: 'info.contrastText'
                            }} 
                          />
                        )}
                        {tiebreaker.is_final && tiebreaker.home_score !== undefined && tiebreaker.away_score !== undefined && (
                          <Typography variant="caption" color="success.main" sx={{ fontSize: '0.7rem' }}>
                            • Final: {tiebreaker.home_score + tiebreaker.away_score} pts
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1} sx={{ ml: 1 }}>
                      {!locked && !viewingPreviousWeek ? (
                        <TextField
                          placeholder="Total pts"
                          type="number"
                          value={tiebreakerPredictions[tiebreaker.id] || ''}
                          onChange={(e) => handleTiebreakerPredictionChange(tiebreaker.id, e.target.value)}
                          inputProps={{ min: 0, max: 200 }}
                          size="small"
                          sx={{ 
                            width: 90,
                            '& .MuiInputBase-input': { 
                              fontSize: '0.75rem',
                              py: 0.5,
                              px: 0.75
                            }
                          }}
                        />
                      ) : (
                        tiebreakerPredictions[tiebreaker.id] && (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Check color="success" sx={{ fontSize: 14 }} />
                            <Typography variant="caption" color="success.main" sx={{ fontSize: '0.7rem' }}>
                              {tiebreakerPredictions[tiebreaker.id]} pts
                            </Typography>
                          </Box>
                        )
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      {!locked && !viewingPreviousWeek && (
        <Box mt={3} display="flex" justifyContent="center">
          <Button
            variant="contained"
            size="large"
            startIcon={<Save />}
            onClick={handleSavePicksClick}
            disabled={saving || progress.picked === 0}
          >
            {saving ? 'Saving...' : 'Save All Picks'}
          </Button>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>Confirm Your Picks</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            You are about to save {progress.picked} picks for Week {selectedWeek.week_number}.
          </Typography>
          {tiebreakerGames.length > 0 && (
            <Typography gutterBottom>
              Tiebreaker predictions: {Object.values(tiebreakerPredictions).filter(pred => pred > 0).length} of {tiebreakerGames.length}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            You can still modify your picks until {(() => {
              let date = dayjs(selectedWeek.picks_lock_time);
              if (!selectedWeek.picks_lock_time.includes('Z') && !selectedWeek.picks_lock_time.includes('+') && !selectedWeek.picks_lock_time.includes('-')) {
                date = dayjs.utc(selectedWeek.picks_lock_time);
              }
              return date.tz('America/New_York').format('ddd, MMM D @ h:mm A z');
            })()}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button 
            onClick={savePicks} 
            variant="contained" 
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          >
            {saving ? 'Saving...' : 'Save Picks'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* JoeB Rule #1 Dialog */}
      <Dialog 
        open={joebRuleDialog} 
        onClose={() => setJoebRuleDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: '#ff9800', 
          color: 'white',
          textAlign: 'center',
          fontWeight: 'bold'
        }}>
          ⚠️ JoeB Rule #1 Warning ⚠️
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#ff9800', fontWeight: 'bold' }}>
            Incomplete Pick Set Detected!
          </Typography>
          <Typography gutterBottom>
            You have only made <strong>{games.filter(game => game.user_pick?.selected_team).length}</strong> picks 
            out of <strong>{games.length}</strong> total games for this week.
          </Typography>
          <Typography gutterBottom>
            <strong>JoeB Rule #1</strong> strongly recommends making picks for ALL games to maximize your scoring potential.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
            Missing picks = missed opportunities for points!
          </Typography>
          <Typography variant="h6" sx={{ mt: 2, color: '#d32f2f' }}>
            Are you sure you want to submit an incomplete set of picks?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setJoebRuleDialog(false)}
            variant="outlined"
            color="primary"
            size="large"
          >
            Go Back & Complete Picks
          </Button>
          <Button 
            onClick={proceedWithIncompletePicks}
            variant="contained"
            color="warning"
            size="large"
            startIcon={<Save />}
          >
            Submit Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MakePicks;
